'use client';
import React, { useState, useCallback } from 'react';
import { Bitcoin, Coins, TrendingUp, AlertTriangle, Layers, Lock } from 'lucide-react';
import KpiCard from './KpiCard';
import PriceChart from './PriceChart';
import VolumeChart from './VolumeChart';
import SupplyChart from './SupplyChart';
import TransactionFeed from './TransactionFeed';
import BtcAddressCard from './BtcAddressCard';
import AutoRefreshBar from './AutoRefreshBar';

// BACKEND INTEGRATION: Replace with real FFCT token stats from token API / CoinGecko
const getTokenStats = () => ({
  price: 0.000369,
  priceChange24h: 5.41,
  volume24h: 3_218_400,
  volumeChange24h: -8.23,
  marketCap: 18_450_000,
  marketCapChange24h: 4.87,
  liquidSupply: 50_000_000_000,
  reservedSupply: 250_000_000_000,
  totalSupply: 300_000_000_000,
  btcPrice: 84_790,
  btcChange24h: 1.32,
  txFeeRate: 0.0015,
  activeDeals: 9,
});

function getLastUpdatedString() {
  const now = new Date();
  return `${now?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

export default function DashboardContent() {
  const [stats, setStats] = useState(getTokenStats());
  const [lastUpdated, setLastUpdated] = useState('4:06:47 PM');

  const handleRefresh = useCallback(() => {
    // BACKEND INTEGRATION: Fetch fresh token stats here
    const newStats = {
      ...getTokenStats(),
      price: 0.000369 + (Math.floor(Math.random() * 3) - 1) * 0.000005,
    };
    setStats(newStats);
    setLastUpdated(getLastUpdatedString());
  }, []);

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Token Dashboard</h1>
          <p className="text-sm text-zinc-400 mt-1">
            FastFoodCrypto (FFCT) · Bitcoin Network · AI Digital Currency
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="badge-gold text-xs">
            <Bitcoin size={10} />
            BTC Network
          </span>
          <span className="badge-active text-xs">Live</span>
        </div>
      </div>
      {/* Auto-refresh bar */}
      <AutoRefreshBar onRefresh={handleRefresh} lastUpdated={lastUpdated} />
      {/* KPI Bento Grid — 6 cards: 2-col hero + 4 regular → 4-col grid */}
      {/* Grid plan: grid-cols-2 md:grid-cols-4 → row1: hero(span2) + 2 regular, row2: 3 regular */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
        {/* Hero — FFCT Price spanning 2 cols */}
        <div className="col-span-1 sm:col-span-2 md:col-span-2 rounded-xl border border-btcgold-500/30 bg-btcgold-500/5 p-5 glow-primary-sm flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium tracking-widest uppercase text-zinc-500">
              FFCT Price
            </span>
            <span className="badge-gold text-xs">
              <Coins size={10} />
              FFCT/USD
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-4xl font-bold text-btcgold-400 tabular-nums font-mono">
              ${stats?.price?.toFixed(6)}
            </span>
            <span className="text-xs text-zinc-500 font-mono">FastFoodCrypto Token</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`flex items-center gap-1 text-sm font-semibold ${
                stats?.priceChange24h >= 0 ? 'text-green-400' : 'text-red-400'
              }`}
            >
              <TrendingUp size={14} />
              {stats?.priceChange24h >= 0 ? '+' : ''}
              {stats?.priceChange24h?.toFixed(2)}% 24h
            </span>
            <span className="text-xs text-zinc-500">vs open $0.000312</span>
          </div>
        </div>

        {/* BTC Reference Price */}
        <KpiCard
          label="BTC Price"
          value={`$${stats?.btcPrice?.toLocaleString()}`}
          change={stats?.btcChange24h}
          changeLabel="24h"
          icon={<Bitcoin size={16} />}
          mono
        />

        {/* Market Cap */}
        <KpiCard
          label="Market Cap"
          value={`$${(stats?.marketCap / 1_000_000)?.toFixed(2)}M`}
          change={stats?.marketCapChange24h}
          changeLabel="24h"
          icon={<Layers size={16} />}
        />

        {/* 24h Volume — with negative change to show realism */}
        <KpiCard
          label="24h Volume"
          value={`$${(stats?.volume24h / 1_000_000)?.toFixed(2)}M`}
          subValue={`${(stats?.volume24h)?.toLocaleString()} FFCT`}
          change={stats?.volumeChange24h}
          changeLabel="vs yesterday"
          alert
          icon={<AlertTriangle size={16} />}
        />

        {/* Liquid Supply */}
        <KpiCard
          label="Liquid Supply"
          value="50B FFCT"
          subValue="Actively tradeable"
          icon={<Coins size={16} />}
          mono
        />

        {/* Reserved Supply */}
        <KpiCard
          label="Reserved Supply"
          value="250B FFCT"
          subValue="Owner's Coinbase wallet"
          icon={<Lock size={16} />}
          mono
        />
      </div>
      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
        <div className="col-span-1 lg:col-span-2">
          <PriceChart />
        </div>
        <div className="col-span-1">
          <SupplyChart />
        </div>
      </div>
      {/* Volume chart full width */}
      <VolumeChart />
      {/* Bottom row: tx feed + btc address */}
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
        <div className="col-span-1 lg:col-span-2">
          <TransactionFeed />
        </div>
        <div className="col-span-1 flex flex-col gap-4">
          <BtcAddressCard />

          {/* Token info card */}
          <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
            <h3 className="text-sm font-semibold text-white">Token Info</h3>
            <div className="flex flex-col gap-3">
              {[
                { label: 'Token Name', value: 'FastFoodCrypto' },
                { label: 'Ticker', value: 'FFCT' },
                { label: 'Network', value: 'Bitcoin' },
                { label: 'Total Supply', value: '300,000,000,000' },
                { label: 'Liquid', value: '50,000,000,000' },
                { label: 'Reserved', value: '250,000,000,000' },
                { label: 'Tx Fee Rate', value: '0.15%' },
                { label: 'AI Token', value: 'Yes' },
              ]?.map((item) => (
                <div key={`info-${item?.label}`} className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">{item?.label}</span>
                  <span className="text-xs font-mono font-medium text-zinc-200">{item?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}